import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Users, FileText, Mail, TrendingUp, Filter, Rocket } from "lucide-react";
import { API } from "../config/env";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

const QUOTE_STATUSES = ["submitted", "in-review", "quoted", "advance_paid", "won", "lost", "closed"];
const INQUIRY_STATUSES = ["new", "responded", "converted", "archived"];

const STATUS_COLOR = {
  submitted: "border-[color:var(--gold)] text-[color:var(--gold)]",
  "in-review": "border-[color:var(--teal)] text-[color:var(--teal)]",
  quoted: "border-[color:var(--purple)] text-[color:var(--purple)]",
  advance_paid: "border-emerald-500 text-emerald-500",
  won: "border-emerald-500 text-emerald-500",
  lost: "border-red-500 text-red-500",
  closed: "border-muted-foreground text-muted-foreground",
  new: "border-[color:var(--gold)] text-[color:var(--gold)]",
  responded: "border-[color:var(--teal)] text-[color:var(--teal)]",
  converted: "border-emerald-500 text-emerald-500",
  archived: "border-muted-foreground text-muted-foreground",
};

export default function AdminPage() {
  const { user, loading, isAuthenticated, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [advanceAmounts, setAdvanceAmounts] = useState({});

  const load = useCallback(async () => {
    try {
      const [s, q, i, u, p] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/quotes`),
        axios.get(`${API}/admin/inquiries`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/projects`),
      ]);
      setStats(s.data); setQuotes(q.data); setInquiries(i.data); setUsers(u.data); setProjects(p.data);
    } catch (e) {
      toast.error("Could not load admin data.");
    }
  }, []);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (loading) return <div className="pt-40 text-center text-muted-foreground" data-testid="admin-auth-loading">Loading…</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/portal" replace />;

  const updateQuoteStatus = async (id, status, advanceAmount) => {
    try {
      const body = { status };
      if (advanceAmount !== undefined && advanceAmount !== "" && advanceAmount !== null) {
        body.advance_amount = Number(advanceAmount);
      }
      await axios.patch(`${API}/admin/quotes/${id}/status`, body);
      toast.success("Quote status updated.");
      load();
    } catch { toast.error("Update failed."); }
  };
  const updateInquiryStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${id}/status`, { status });
      toast.success("Inquiry status updated.");
      load();
    } catch { toast.error("Update failed."); }
  };

  const filteredQuotes = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="admin-page">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)] mb-2">Admin Console · v1.0</div>
            <h1 className="display display-section text-5xl md:text-6xl tracking-[-0.03em]">
              <em className="italic text-[color:var(--gold)]">Control</em> room.
            </h1>
            <div className="mt-3 text-sm text-muted-foreground">{user.email} · {user.role}</div>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-sm" data-testid="admin-logout">
            <LogOut size={14} /> Logout
          </Button>
        </div>

        {stats && (
          <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatBlock icon={Users} label="Total users" value={stats.users} accent="var(--steel)" />
            <StatBlock icon={FileText} label="Total quotes" value={stats.quotes} accent="var(--gold)" />
            <StatBlock icon={TrendingUp} label="Open quotes" value={stats.open_quotes} accent="var(--teal)" />
            <StatBlock icon={Mail} label="Total inquiries" value={stats.inquiries} accent="var(--purple)" />
            <StatBlock icon={Mail} label="New inquiries" value={stats.new_inquiries} accent="var(--gold)" />
          </div>
        )}

        <Tabs defaultValue="quotes" className="mt-12">
          <TabsList className="rounded-sm">
            <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Quotes · {quotes.length}</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries">Inquiries · {inquiries.length}</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users · {users.length}</TabsTrigger>
            <TabsTrigger value="projects" data-testid="admin-tab-projects">Projects · {projects.length}</TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">§ All quotes</div>
              <div className="flex items-center gap-2">
                <Filter size={14} className="text-muted-foreground" />
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-40 rounded-sm h-9" data-testid="quote-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Advance ₹</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredQuotes.map((q) => (
                    <TableRow key={q.id} data-testid={`admin-quote-row-${q.reference}`}>
                      <TableCell className="font-mono-2 text-xs text-[color:var(--gold)]">{q.reference}</TableCell>
                      <TableCell>
                        <div className="font-medium">{q.user_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{q.user_email}</div>
                        <div className="text-xs text-muted-foreground">{q.user_company || "—"}</div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="font-medium">{q.project_name}</div>
                        {q.notes && <div className="text-xs text-muted-foreground truncate max-w-xs">{q.notes}</div>}
                      </TableCell>
                      <TableCell className="text-xs">{q.industry || "—"}</TableCell>
                      <TableCell className="text-xs">{q.items?.length || 0}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`rounded-sm ${STATUS_COLOR[q.status] || ""}`}>{q.status}</Badge>
                        {q.status === "advance_paid" && q.project_reference && (
                          <div className="text-[10px] font-mono-2 text-emerald-500 mt-1">{q.project_reference}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          placeholder="e.g. 15000"
                          value={advanceAmounts[q.id] ?? q.advance_amount ?? ""}
                          onChange={(e) => setAdvanceAmounts((m) => ({ ...m, [q.id]: e.target.value }))}
                          onBlur={(e) => {
                            if (e.target.value !== "" && Number(e.target.value) !== q.advance_amount) {
                              updateQuoteStatus(q.id, q.status, e.target.value);
                            }
                          }}
                          className="w-28 h-8 text-xs rounded-sm"
                          data-testid={`quote-advance-input-${q.reference}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <QuoteDetailDialog quote={q} />
                          <Select value={q.status} onValueChange={(v) => updateQuoteStatus(q.id, v, advanceAmounts[q.id] ?? q.advance_amount)}>
                            <SelectTrigger className="w-32 h-8 text-xs rounded-sm" data-testid={`quote-status-${q.reference}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredQuotes.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">No quotes for filter.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="inquiries" className="mt-6">
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">§ Public inquiries</div>
            <div className="border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name / Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inquiries.map((i) => (
                    <TableRow key={i.id} data-testid={`admin-inquiry-row-${i.id}`}>
                      <TableCell>
                        <div className="font-medium">{i.name}</div>
                        <div className="text-xs text-muted-foreground">{i.company || "—"}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs"><a href={`mailto:${i.email}`}>{i.email}</a></div>
                        <div className="text-xs text-muted-foreground">{i.phone || "—"}</div>
                      </TableCell>
                      <TableCell className="max-w-md">
                        <div className="text-xs line-clamp-3">{i.message}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`rounded-sm ${STATUS_COLOR[i.status] || ""}`}>{i.status || "new"}</Badge>
                      </TableCell>
                      <TableCell>
                        <Select value={i.status || "new"} onValueChange={(v) => updateInquiryStatus(i.id, v)}>
                          <SelectTrigger className="w-32 h-8 text-xs rounded-sm" data-testid={`inquiry-status-${i.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {INQUIRY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                  {inquiries.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No inquiries yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">§ Client accounts</div>
            <div className="border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-xs">{u.email}</TableCell>
                      <TableCell className="text-xs">{u.company || "—"}</TableCell>
                      <TableCell className="text-xs">{u.phone || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`rounded-sm ${u.role === "admin" ? STATUS_COLOR["submitted"] : ""}`}>{u.role}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="projects" className="mt-6">
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">§ Projects created from advance payments</div>
            <div className="border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Quote Ref.</TableHead>
                    <TableHead>Advance ₹</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map((p) => (
                    <TableRow key={p.id} data-testid={`admin-project-row-${p.reference}`}>
                      <TableCell className="font-mono-2 text-xs text-[color:var(--teal)]"><Rocket size={12} className="inline mr-1" />{p.reference}</TableCell>
                      <TableCell className="text-xs">{p.user_email}</TableCell>
                      <TableCell className="font-medium">{p.project_name}</TableCell>
                      <TableCell className="font-mono-2 text-xs text-[color:var(--gold)]">{p.quote_reference || "—"}</TableCell>
                      <TableCell className="text-xs">₹{Number(p.advance_amount || 0).toLocaleString("en-IN")}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="rounded-sm border-emerald-500 text-emerald-500 capitalize">{p.status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {projects.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No projects created yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatBlock({ icon: Icon, label, value, accent }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="p-5 clip-frame-sm border border-border bg-background"
                data-testid={`admin-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="font-mono-2 text-[10px] tracking-[0.25em] uppercase text-muted-foreground">{label}</div>
        <Icon size={14} style={{ color: accent }} />
      </div>
      <div className="mt-3 display text-4xl leading-none" style={{ color: accent }}>{value}</div>
    </motion.div>
  );
}

function QuoteDetailDialog({ quote }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" data-testid={`quote-view-${quote.reference}`}>View</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-xl">
        <DialogHeader>
          <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)]">{quote.reference}</div>
          <DialogTitle className="display display-section text-2xl mt-2">{quote.project_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div><span className="text-muted-foreground">Client:</span> {quote.user_name} · {quote.user_email} · {quote.user_company || "—"}</div>
          <div><span className="text-muted-foreground">Industry:</span> {quote.industry || "—"}</div>
          <div><span className="text-muted-foreground">Status:</span> {quote.status}</div>
          <div><span className="text-muted-foreground">Created:</span> {new Date(quote.created_at).toLocaleString()}</div>
          <div>
            <div className="text-muted-foreground mb-1">Items:</div>
            <ul className="space-y-1 pl-4 list-disc">
              {quote.items?.map((it, idx) => (
                <li key={idx}>{it.description} · <span className="text-muted-foreground">Qty: {it.quantity}</span></li>
              ))}
            </ul>
          </div>
          {quote.notes && (
            <div>
              <div className="text-muted-foreground mb-1">Notes:</div>
              <div className="p-3 bg-secondary/60 rounded-sm text-xs whitespace-pre-wrap">{quote.notes}</div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
